<script setup>
// NOTICE: KAPTION-INTEGRATION
// Step 3: Review & Confirm with enhanced preview (renderBind/bind modes)
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import useTemplateVariables from 'dashboard/composables/useTemplateVariables';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  scheduledAt: {
    type: String,
    default: null,
  },
  inboxId: {
    type: [Number, String],
    required: true,
  },
  templateId: {
    type: [Number, String],
    required: true,
  },
  selectedAudience: {
    type: Array,
    required: true,
  },
  contactCount: {
    type: Number,
    default: 0,
  },
  variableBindings: {
    type: Object,
    default: () => ({}),
  },
  sampleContacts: {
    type: Array,
    default: () => [],
  },
  // NOTICE: KAPTION-INTEGRATION - Card title (tap_target): shown in the
  // preview as the tappable link card (title + tracked-link domain).
  tapTargetTitle: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

// Selected contact for preview
const selectedContactId = ref(null);

// Highlight toggle: false = renderBind (final), true = bind (highlighted)
// NOTICE: KAPTION-INTEGRATION - Default to true to show highlights by default
const showHighlights = ref(true);
const iframeMode = computed(() =>
  showHighlights.value ? 'bind' : 'renderBind'
);

// NOTICE: KAPTION-INTEGRATION - Truncation toggle for MARKETING templates
// Shows "Read more" preview for templates with 5+ lines
const showTruncation = ref(true);

// Store getters
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const labels = useMapGetter('labels/getLabels');
const segments = useMapGetter('customViews/getContactCustomViews');
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');

// Computed
const selectedTemplate = computed(() => {
  if (!props.inboxId || !props.templateId) return null;
  const templates = getFilteredWhatsAppTemplates.value(props.inboxId);
  return templates.find(tmpl => tmpl.id === props.templateId);
});

const selectedInbox = computed(() => {
  return inboxes.value.find(i => i.id === props.inboxId);
});

// NOTICE: KAPTION-INTEGRATION
// Build audience display from both labels and segments
const selectedAudienceLabel = computed(() => {
  if (!props.selectedAudience.length) return '';

  const names = [];
  props.selectedAudience.forEach(entry => {
    if (entry.type === 'Label') {
      const label = labels.value.find(l => l.id === entry.id);
      if (label) names.push(label.title);
    } else if (entry.type === 'Segment') {
      const segment = segments.value?.find(s => s.id === entry.id);
      if (segment) names.push(segment.name);
    }
  });

  return names.join(', ') || '';
});

const templateText = computed(() => {
  if (!selectedTemplate.value) return '';
  const bodyComponent = selectedTemplate.value.components.find(
    c => c.type === 'BODY'
  );
  return bodyComponent ? bodyComponent.text : '';
});

// NOTICE: KAPTION-INTEGRATION - Check if template is MARKETING category
// MARKETING templates show "Read more" truncation when message is 5+ lines
const isMarketingTemplate = computed(() => {
  return selectedTemplate.value?.category === 'MARKETING';
});

// Extract variables from template
const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;

const templateVariables = computed(() => {
  if (!templateText.value) return [];
  const matches = Array.from(templateText.value.matchAll(VARIABLE_PATTERN));
  const keys = matches.map(match => match[1].trim());
  return [...new Set(keys)];
});

// Variable suggestions for available bindings
const { variableSuggestions } = useTemplateVariables({
  sampleContacts: computed(() => props.sampleContacts),
});

const availableBindingsForSlot = computed(() => {
  const suggestions = variableSuggestions.value || [];
  return suggestions
    .filter(s => s.key?.startsWith('contact.'))
    .map(s => ({
      value: `{{ ${s.key} }}`,
      label: s.label,
    }));
});

// Build variables array for the iframe
const iframeVariables = computed(() => {
  const bindings = props.variableBindings?.body || {};

  return templateVariables.value.map(slot => ({
    slot,
    binding: bindings[slot] || null,
    availableBindings: availableBindingsForSlot.value,
  }));
});

// Get the currently selected contact
const selectedContact = computed(() => {
  if (!selectedContactId.value || !props.sampleContacts.length) {
    return props.sampleContacts[0] || null;
  }
  return (
    props.sampleContacts.find(c => c.id === selectedContactId.value) ||
    props.sampleContacts[0]
  );
});

// Build resolved values for preview
const resolvedValues = computed(() => {
  const contact = selectedContact.value;
  if (!contact) return {};

  const values = {};

  // Map common contact fields
  values['{{ contact.name }}'] = contact.name || '';
  values['{{ contact.first_name }}'] =
    contact.name?.split(' ')[0] || contact.name || '';
  values['{{ contact.last_name }}'] =
    contact.name?.split(' ').slice(1).join(' ') || '';
  values['{{ contact.email }}'] = contact.email || '';
  values['{{ contact.phone_number }}'] = contact.phone_number || '';

  // Add custom attributes
  if (contact.custom_attributes) {
    Object.entries(contact.custom_attributes).forEach(([key, val]) => {
      values[`{{ contact.custom_attribute.${key} }}`] = String(val);
    });
  }

  return values;
});

const formattedSchedule = computed(() => {
  if (!props.scheduledAt) return 'Not scheduled';

  const date = new Date(props.scheduledAt);
  const now = new Date();

  // Check if it's "now" (within 1 minute)
  const diffMs = date.getTime() - now.getTime();
  if (Math.abs(diffMs) < 60000) {
    return 'Immediately';
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
});

// Select a contact for preview
const selectContact = contactId => {
  selectedContactId.value = contactId;
};

// Initialize selected contact when sampleContacts changes
watch(
  () => props.sampleContacts,
  newContacts => {
    if (newContacts.length > 0 && !selectedContactId.value) {
      selectedContactId.value = newContacts[0].id;
    }
  },
  { immediate: true }
);

// Get initials from name
const getInitials = name => {
  if (!name) return 'C';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Get display name or fallback
const getDisplayName = contact => {
  return contact?.name || contact?.email || contact?.phone_number || 'Contact';
};

// Get subtitle (phone or email)
const getContactSubtitle = contact => {
  if (contact?.phone_number) return contact.phone_number;
  if (contact?.email) return contact.email;
  return '';
};
</script>

<template>
  <div class="grid grid-cols-5 gap-6 h-full">
    <!-- Left Column: Summary + Recipients (3/5 width) -->
    <div class="col-span-3 flex flex-col gap-4">
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <!-- Campaign Details Summary (2x2 grid at TOP) - clean, no background -->
      <div class="grid grid-cols-2 gap-x-8 gap-y-3">
        <div class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-11 uppercase tracking-wide">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SUMMARY.CAMPAIGN') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ title }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-11 uppercase tracking-wide">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SUMMARY.TEMPLATE') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ selectedTemplate?.name || 'N/A' }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-11 uppercase tracking-wide">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SUMMARY.AUDIENCE') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ selectedAudienceLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-11 uppercase tracking-wide">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SUMMARY.SCHEDULE') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ formattedSchedule }}
          </span>
        </div>
      </div>

      <!-- Ready to Send Banner -->
      <div
        class="flex items-start gap-3 p-4 bg-n-alpha-blue1 border border-n-blue-6 rounded-lg"
      >
        <span
          class="i-lucide-info text-n-blue-11 flex-shrink-0 mt-0.5 w-5 h-5"
        />
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-n-blue-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.READY_TO_SEND') }}
          </p>
          <p class="text-sm text-n-blue-11">
            {{
              t(
                'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.READY_TO_SEND_DESCRIPTION',
                {
                  count: contactCount,
                  plural: contactCount !== 1 ? 's' : '',
                  schedule:
                    formattedSchedule === 'Immediately'
                      ? t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.IMMEDIATELY')
                      : t(
                          'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.ON_SCHEDULE',
                          { schedule: formattedSchedule }
                        ),
                }
              )
            }}
          </p>
        </div>
      </div>

      <!-- Recipients Header -->
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SAMPLE_RECIPIENTS') }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SHOWING_RECIPIENTS', {
              shown: sampleContacts.length,
              total: contactCount,
            })
          }}
        </span>
      </div>

      <!-- Recipients List - Clean, minimal styling -->
      <div class="flex flex-col gap-1 overflow-y-auto">
        <button
          v-for="contact in sampleContacts"
          :key="contact.id"
          type="button"
          class="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left"
          :class="[
            selectedContactId === contact.id
              ? 'bg-n-alpha-brand1'
              : 'hover:bg-n-alpha-black2',
          ]"
          @click="selectContact(contact.id)"
        >
          <!-- Avatar -->
          <div
            class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
            :class="[
              selectedContactId === contact.id
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 text-n-slate-11',
            ]"
          >
            {{ getInitials(contact.name) }}
          </div>

          <!-- Contact Info -->
          <div class="flex flex-col min-w-0 flex-1">
            <span
              class="text-sm font-medium truncate"
              :class="[
                selectedContactId === contact.id
                  ? 'text-n-brand'
                  : 'text-n-slate-12',
              ]"
            >
              {{ getDisplayName(contact) }}
            </span>
            <span class="text-xs text-n-slate-11 truncate">
              {{ getContactSubtitle(contact) }}
            </span>
          </div>

          <!-- Selected Indicator -->
          <span
            v-if="selectedContactId === contact.id"
            class="i-lucide-check w-5 h-5 text-n-brand flex-shrink-0"
          />
        </button>

        <!-- Empty State -->
        <div
          v-if="sampleContacts.length === 0"
          class="flex items-center justify-center p-8"
        >
          <span class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.NO_CONTACTS') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Right Column: Full Phone Preview (2/5 width) -->
    <div class="col-span-2 flex flex-col items-center justify-start gap-3">
      <!-- Preview Header with Toggles -->
      <div class="flex items-center justify-between w-full">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.MESSAGE_PREVIEW') }}
        </h3>

        <div class="flex items-center gap-4">
          <!-- NOTICE: KAPTION-INTEGRATION - Truncation Toggle for MARKETING templates -->
          <label
            v-if="isMarketingTemplate"
            class="flex items-center gap-2 cursor-pointer"
          >
            <span class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.SHOW_TRUNCATION') }}
            </span>
            <Switch v-model="showTruncation" />
          </label>

          <!-- NOTICE: KAPTION-INTEGRATION - Highlight Toggle using standard Switch component -->
          <label class="flex items-center gap-2 cursor-pointer">
            <span class="text-xs text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.HIGHLIGHT_VARIABLES')
              }}
            </span>
            <Switch v-model="showHighlights" />
          </label>
        </div>
      </div>

      <!-- Mode Description -->
      <p class="text-xs text-n-slate-11 w-full -mt-1">
        <template v-if="showHighlights">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.HIGHLIGHT_DESCRIPTION')
          }}
        </template>
        <template v-else>
          {{
            t(
              'CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_3.FINAL_PREVIEW_DESCRIPTION'
            )
          }}
        </template>
      </p>

      <!-- NOTICE: KAPTION-INTEGRATION - Enhanced WhatsApp Preview with renderBind/bind modes -->
      <!-- Truncate is enabled for MARKETING templates when user toggles it on -->
      <KaptionWhatsAppPreviewEnhanced
        :template="selectedTemplate"
        :variables="iframeVariables"
        :resolved-values="resolvedValues"
        :tap-target-title="tapTargetTitle"
        :mode="iframeMode"
        :loading="false"
        :bare="false"
        :company-name="selectedInbox?.name || 'Business'"
        :truncate="isMarketingTemplate && showTruncation"
        class="w-full h-[520px]"
      />
    </div>
  </div>
</template>
